(() => {
  const ROOT_SELECTOR = '[data-cork-multi-variant]';

  class CorkMultiVariant {
    constructor(root) {
      this.root = root;
      this.root.dataset.corkReady = 'true';
      this.form = root.closest('form');
      this.productForm = root.closest('product-form');
      this.productPage = root.closest('[data-js-product-component]');
      this.quantity = this.form?.querySelector('input[name="quantity"]');
      this.variantInput = this.form?.querySelector('input[name="id"]');
      this.list = root.querySelector('[data-cork-unit-list]');
      this.error = root.querySelector('[data-cork-multi-error]');
      this.product = JSON.parse(root.querySelector('[data-cork-product-json]').textContent);
      this.variants = (this.product.variants || []).filter(variant => variant.available);
      this.optionNames = (this.product.options || []).map(option => typeof option === 'string' ? option : option.name);
      this.unitVariantIds = [];
      this.maxQuantity = Math.max(1, Number.parseInt(root.dataset.maxQuantity, 10) || 4);
      this.pricePreviewTimer = null;
      this.pricePreviewFallbackTimer = null;
      this.pricePreviewRequest = 0;
      this.pricePreviewCache = new Map();
      this.prefixTotalCache = new Map();
      this.prefixTotalRequests = new Map();

      if (!this.form || !this.quantity || !this.variantInput || !this.list || !this.variants.length) return;

      this.quantity.max = String(this.maxQuantity);
      if (Number(this.quantity.value) > this.maxQuantity) this.quantity.value = String(this.maxQuantity);

      this.form.addEventListener('input', event => {
        if (event.target === this.quantity && event.target.value !== '') this.render();
      });
      this.form.addEventListener('change', event => {
        if (event.target === this.quantity) this.render();
      });
      this.form.addEventListener('click', event => {
        if (event.target.closest('.qty-plus, .qty-minus')) setTimeout(() => this.render(), 0);
      });
      this.form.addEventListener('submit', event => this.submit(event), true);

      const picker = this.productPage?.querySelector('product-variants');
      if (picker) {
        picker.addEventListener('VARIANT_CHANGE', () => {
          this.updateUnitPrices();
          this.scheduleInitialPreview();
        });
      }

      this.render();
      this.scheduleInitialPreview();
    }

    getQuantity() {
      const value = Math.max(1, Math.min(this.maxQuantity, Number.parseInt(this.quantity.value, 10) || 1));
      if (String(value) !== this.quantity.value) this.quantity.value = String(value);
      return value;
    }

    getFirstVariant() {
      const id = String(this.variantInput.value);
      return this.variants.find(variant => String(variant.id) === id) || null;
    }

    getVariant(id) {
      return this.variants.find(variant => String(variant.id) === String(id)) || null;
    }

    renderUnitPrice(element, variant) {
      element.replaceChildren();
      element.removeAttribute('aria-busy');
      element.classList.remove('is-calculating');
      element.textContent = this.money(variant.price);
    }

    renderCalculatedPrice(element, originalCents, finalCents) {
      element.replaceChildren();
      element.removeAttribute('aria-busy');
      element.classList.remove('is-calculating');

      if (finalCents >= originalCents) {
        element.textContent = this.money(finalCents);
        return;
      }

      const originalPrice = document.createElement('s');
      originalPrice.className = 'cork-multi-unit__price-original';
      originalPrice.textContent = this.money(originalCents);

      const discountedPrice = document.createElement('strong');
      discountedPrice.className = 'cork-multi-unit__price-discounted';
      discountedPrice.textContent = this.money(finalCents);

      const discountLabel = document.createElement('small');
      discountLabel.className = 'cork-multi-unit__price-label';
      discountLabel.textContent = 'Precio con descuento';

      element.append(originalPrice, discountedPrice, discountLabel);
    }

    selectedUnits() {
      const quantity = this.getQuantity();
      const firstVariant = this.getFirstVariant();
      if (!firstVariant) return [];

      const units = [{ unit: 1, variant: firstVariant }];
      for (let unit = 2; unit <= quantity; unit += 1) {
        const variant = this.getVariant(this.unitVariantIds[unit]);
        if (!variant) return [];
        units.push({ unit, variant });
      }
      return units;
    }

    previewKey(units) {
      return units.map(({ variant }) => String(variant.id)).join(':');
    }

    prefixKey(units) {
      return this.previewKey(units);
    }

    scheduleInitialPreview() {
      const firstVariant = this.getFirstVariant();
      if (!firstVariant) return;
      const units = [
        { unit: 1, variant: firstVariant },
        { unit: 2, variant: firstVariant }
      ];
      const run = () => this.getPreviewPrices(units).catch(() => {});

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 1200 });
      } else {
        window.setTimeout(run, 350);
      }
    }

    schedulePricePreview() {
      window.clearTimeout(this.pricePreviewTimer);
      window.clearTimeout(this.pricePreviewFallbackTimer);
      const requestId = ++this.pricePreviewRequest;
      const units = this.selectedUnits();
      if (units.length < 2) return;

      const cached = this.pricePreviewCache.get(this.previewKey(units));
      if (cached) {
        this.applyPricePreview(cached, requestId);
        return;
      }

      this.list.querySelectorAll('[data-cork-unit-price]').forEach(element => {
        element.classList.add('is-calculating');
        element.setAttribute('aria-busy', 'true');
      });

      this.pricePreviewTimer = window.setTimeout(() => {
        if (requestId !== this.pricePreviewRequest) return;
        this.calculatePricePreview(units, requestId);
      }, 75);

      this.pricePreviewFallbackTimer = window.setTimeout(() => {
        if (requestId !== this.pricePreviewRequest) return;
        this.pricePreviewRequest += 1;
        this.updateUnitPrices(false);
      }, 6000);
    }

    async fetchJson(url, options = {}, timeoutMs = 5000) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        const result = await response.json();
        return { response, result };
      } finally {
        window.clearTimeout(timeout);
      }
    }

    async fetchPrefixTotal(units) {
      const key = this.prefixKey(units);
      if (this.prefixTotalCache.has(key)) return this.prefixTotalCache.get(key);
      if (this.prefixTotalRequests.has(key)) return this.prefixTotalRequests.get(key);

      const request = (async () => {
        const endpoint = `${window.Shopify?.routes?.root || '/'}cart/add.js`;
        const previewBatch = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const items = units.map(({ unit, variant }) => ({
          id: Number(variant.id),
          quantity: 1,
          properties: {
            _cork_preview_unit: String(unit),
            _cork_preview_batch: previewBatch
          }
        }));
        const { response, result } = await this.fetchJson(endpoint, {
          method: 'POST',
          credentials: 'omit',
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        }, 5500);

        if (!response.ok || !Array.isArray(result.items)) {
          throw new Error(result.description || result.message || 'Shopify no pudo calcular los precios.');
        }

        const total = result.items.reduce((sum, item) => {
          const lineTotal = Number.isFinite(Number(item.final_line_price))
            ? Number(item.final_line_price)
            : Number(item.line_price);
          return sum + (Number.isFinite(lineTotal) ? lineTotal : 0);
        }, 0);

        if (!Number.isFinite(total) || total <= 0) {
          throw new Error('Shopify devolvió un total de prueba no válido.');
        }
        this.prefixTotalCache.set(key, total);
        return total;
      })();

      this.prefixTotalRequests.set(key, request);
      try {
        return await request;
      } finally {
        this.prefixTotalRequests.delete(key);
      }
    }

    async getPreviewPrices(units) {
      const key = this.previewKey(units);
      if (this.pricePreviewCache.has(key)) return this.pricePreviewCache.get(key);

      const totals = [];
      for (let prefixIndex = 0; prefixIndex < units.length; prefixIndex += 1) {
        totals.push(await this.fetchPrefixTotal(units.slice(0, prefixIndex + 1)));
      }

      const prices = {};
      for (let index = 1; index < units.length; index += 1) {
        const { unit, variant } = units[index];
        prices[unit] = {
          originalCents: Number(variant.price),
          finalCents: Math.max(0, totals[index] - totals[index - 1])
        };
      }
      this.pricePreviewCache.set(key, prices);
      return prices;
    }

    async calculatePricePreview(units, requestId) {
      try {
        const prices = await this.getPreviewPrices(units);
        this.applyPricePreview(prices, requestId);
      } catch (error) {
        if (requestId !== this.pricePreviewRequest) return;
        console.warn('[CORK] No se pudo calcular la previsualización de descuentos:', error);
        this.updateUnitPrices(false);
      }
    }

    applyPricePreview(prices, requestId) {
      if (requestId !== this.pricePreviewRequest) return;
      window.clearTimeout(this.pricePreviewFallbackTimer);
      this.list.querySelectorAll('[data-cork-unit-price]').forEach(element => {
        const unit = Number(element.dataset.corkUnitPrice);
        const price = prices[unit];
        const variant = this.getVariant(this.unitVariantIds[unit]);
        if (price) this.renderCalculatedPrice(element, price.originalCents, price.finalCents);
        else if (variant) this.renderUnitPrice(element, variant);
      });
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
        style: 'currency',
        currency: this.root.dataset.currency || 'EUR'
      }).format(Number(cents) / 100);
    }

    optionValues(index) {
      return [...new Set(this.variants.map(variant => variant.options[index]))];
    }

    render() {
      const quantity = this.getQuantity();
      const firstVariant = this.getFirstVariant();
      const minus = this.form.querySelector('.qty-minus');
      const plus = this.form.querySelector('.qty-plus');
      minus?.classList.toggle('disabled', quantity <= 1);
      plus?.classList.toggle('disabled', quantity >= this.maxQuantity);
      this.root.hidden = quantity < 2;
      this.clearError();

      if (quantity < 2 || !firstVariant) {
        this.list.innerHTML = '';
        return;
      }

      for (let unit = 2; unit <= quantity; unit += 1) {
        if (!this.getVariant(this.unitVariantIds[unit])) this.unitVariantIds[unit] = firstVariant.id;
      }

      this.list.innerHTML = '';
      for (let unit = 2; unit <= quantity; unit += 1) {
        this.list.append(this.buildUnit(unit));
      }
      this.schedulePricePreview();
    }

    buildUnit(unit) {
      const selectedVariant = this.getVariant(this.unitVariantIds[unit]) || this.getFirstVariant();
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'cork-multi-unit';
      fieldset.dataset.corkUnit = String(unit);

      const legend = document.createElement('legend');
      legend.className = 'cork-multi-unit__title';
      legend.textContent = `Funda ${unit}`;
      fieldset.append(legend);

      const fields = document.createElement('div');
      fields.className = 'cork-multi-unit__fields';

      this.optionNames.forEach((name, optionIndex) => {
        const label = document.createElement('label');
        label.className = 'cork-multi-unit__field';
        const labelText = document.createElement('span');
        labelText.textContent = name;
        const select = document.createElement('select');
        select.dataset.optionIndex = String(optionIndex);
        select.setAttribute('aria-label', `${name} de la funda ${unit}`);

        this.optionValues(optionIndex).forEach(value => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          option.selected = selectedVariant.options[optionIndex] === value;
          select.append(option);
        });

        select.addEventListener('change', event => this.changeOption(unit, optionIndex, event.target.value));
        label.append(labelText, select);
        fields.append(label);
      });

      const price = document.createElement('span');
      price.className = 'cork-multi-unit__price';
      price.dataset.corkUnitPrice = String(unit);
      this.renderUnitPrice(price, selectedVariant);
      fields.append(price);
      fieldset.append(fields);
      return fieldset;
    }

    changeOption(unit, changedIndex, value) {
      const fieldset = this.list.querySelector(`[data-cork-unit="${unit}"]`);
      const selections = [...fieldset.querySelectorAll('select')].map(select => select.value);
      selections[changedIndex] = value;

      let variant = this.variants.find(item => item.options.every((option, index) => option === selections[index]));
      if (!variant) {
        const candidates = this.variants.filter(item => item.options[changedIndex] === value);
        variant = candidates.sort((a, b) => {
          const matchesA = a.options.reduce((total, option, index) => total + (index !== changedIndex && option === selections[index] ? 1 : 0), 0);
          const matchesB = b.options.reduce((total, option, index) => total + (index !== changedIndex && option === selections[index] ? 1 : 0), 0);
          return matchesB - matchesA;
        })[0];
      }

      if (!variant) {
        this.showError('Esa combinación no está disponible.');
        return;
      }

      this.unitVariantIds[unit] = variant.id;
      this.render();
    }

    updateUnitPrices(schedule = true) {
      this.list.querySelectorAll('[data-cork-unit-price]').forEach(price => {
        const unit = Number(price.dataset.corkUnitPrice);
        const variant = this.getVariant(this.unitVariantIds[unit]);
        if (variant) this.renderUnitPrice(price, variant);
      });
      if (schedule) this.schedulePricePreview();
    }

    formProperties() {
      const properties = {};
      new FormData(this.form).forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') properties[match[1]] = value;
      });
      return properties;
    }

    async submit(event) {
      const quantity = this.getQuantity();
      if (quantity === 1) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.clearError();

      const firstVariant = this.getFirstVariant();
      if (!firstVariant) {
        this.showError('Selecciona primero el modelo de la funda 1.');
        return;
      }

      const submitButton = this.form.querySelector('[type="submit"]');
      submitButton?.classList.add(`${window.KT_PREFIX || ''}working`);

      const batch = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const sharedProperties = this.formProperties();
      const items = [];

      for (let unit = 1; unit <= quantity; unit += 1) {
        const variant = unit === 1 ? firstVariant : this.getVariant(this.unitVariantIds[unit]);
        if (!variant) {
          this.showError(`Selecciona una combinación válida para la funda ${unit}.`);
          submitButton?.classList.remove(`${window.KT_PREFIX || ''}working`);
          return;
        }
        items.push({
          id: variant.id,
          quantity: 1,
          properties: { ...sharedProperties, _cork_unit: `${batch}-${unit}` }
        });
      }

      try {
        const response = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok || result.status === 422) throw new Error(result.description || result.message || 'No se han podido añadir las fundas.');

        if (document.querySelector('[data-js-site-cart-sidebar]') && typeof window.refreshCart === 'function') {
          window.refreshCart();
          this.productForm?.dispatchEvent(new Event('add-to-cart'));
        } else {
          window.location.href = KROWN.settings.routes.cart_url;
        }
      } catch (error) {
        this.showError(error.message || 'No se han podido añadir las fundas. Inténtalo de nuevo.');
      } finally {
        submitButton?.classList.remove(`${window.KT_PREFIX || ''}working`);
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }
  }

  const init = scope => scope.querySelectorAll(ROOT_SELECTOR).forEach(root => {
    if (root.dataset.corkReady !== 'true') new CorkMultiVariant(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
